<template>
  <div>
    <h2 class="step-name">Beef Trace Elements</h2>

    <div class="step-section">
      <h3 class="text-left">
        <span class="form-group">
          Serum MG
          <comments field-name="serum_mg"></comments>
        </span>
      </h3>
      <template v-if="serum_mg_data.length > 0">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Animal Name</th>
              <th>Description</th>
              <th>Test Type</th>
              <th>avg</th>
              <th># &lt; 0.8</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(t_mg, index) in getChartData('beef_mg_results')" :key="index">
              <td class="text-left">{{ t_mg['Date'] }}</td>
              <td class="text-left">{{ t_mg['Animal Name'] }}</td>
              <td class="text-left">{{ t_mg['Description'] }}</td>
              <td class="text-left">{{ t_mg['Test Type'] }}</td>
              <td class="text-left">{{ t_mg['Average'] }}</td>
              <td class="text-left">{{ t_mg['# &lt; 0.8'] }}</td>
            </tr>
          </tbody>
        </table>

        <box-plot-chart :chart-data="get_mg_data" />
      </template>
      <span v-else>No test data</span>
    </div>

    <div class="step-section">
      <h3 class="text-left">
        <span class="form-group">
          Serum SSE
          <comments field-name="serum_sse"></comments>
        </span>
      </h3>
      <template v-if="sse_data.length > 0">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Animal Name</th>
              <th>Description</th>
              <th>Test Type</th>
              <th>avg</th>
              <th># &lt; 0.8</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(t_sse, index) in getChartData('beef_sse_results')" :key="index">
              <td class="text-left">{{ t_sse['Date'] }}</td>
              <td class="text-left">{{ t_sse['Animal Name'] }}</td>
              <td class="text-left">{{ t_sse['Description'] }}</td>
              <td class="text-left">{{ t_sse['Test Type'] }}</td>
              <td class="text-left">{{ t_sse['Average'] }}</td>
              <td class="text-left">{{ t_sse['# &lt; 0.8'] }}</td>
            </tr>
          </tbody>
        </table>

        <box-plot-chart :chart-data="get_sse_data" />
      </template>

      <span v-else>No test data</span>
    </div>

    <div class="step-section">
      <h3 class="text-left">
        <span class="form-group">
          Serum CU
          <comments field-name="serum_cu"></comments>
        </span>
      </h3>

      <template v-if="cu_data.length > 0">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Animal Name</th>
              <th>Description</th>
              <th>Test Type</th>
              <th>avg</th>
              <th># &lt; 0.8</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(t_cu, index) in getChartData('beef_cu_results')" :key="index">
              <td class="text-left">{{ t_cu['Date'] }}</td>
              <td class="text-left">{{ t_cu['Animal Name'] }}</td>
              <td class="text-left">{{ t_cu['Description'] }}</td>
              <td class="text-left">{{ t_cu['Test Type'] }}</td>
              <td class="text-left">{{ t_cu['Average'] }}</td>
              <td class="text-left">{{ t_cu['# &lt; 0.8'] }}</td>
            </tr>
          </tbody>
        </table>

        <box-plot-chart :chart-data="get_cu_data" />
      </template>
      <span v-else>No test data</span>
    </div>

    <div class="step-section">
      <radio-input
        label="Interested in trace element testing?"
        :add-email-notification="true"
        field-name="beef_wants_te_testing"
        @on-change="toggleNotification"
        :add-comment="true"
        :choices="[
          { value: 'Yes', display: 'Yes' },
          { value: 'No', display: 'No' },
        ]"
      >
      </radio-input>
    </div>

    <div class="step-section">
      <discussion-points
        title="Plan"
        comments-field="plan"
        points="Selenium, Copper, Iodine, Magnesium, Cobalt, Young stock growth"
      >
      </discussion-points>
    </div>

    <drug-allocation-cart :drugGroups="getFieldValue('DRUG_GROUPS')" animalType="beef"> </drug-allocation-cart>
  </div>
</template>

<script>
import baseComponent from '@/mixins/base_consult_component_v2'
import BoxPlotChart from '../charts/BoxPlotChart'
import DrugAllocationCart from './DrugAllocationCart'

export default {
  name: 'BeefTraceElements',
  mixins: [baseComponent],
  components: {
    BoxPlotChart,
    DrugAllocationCart,
  },
  methods: {
    toggleNotification: function ({ fieldName, value }) {
      if (fieldName == 'beef_wants_te_testing') {
        this.setEmailNotification({
          slug: 'beef-interested-in-trace-element-testing',
          value: value == 'Yes' ? true : false,
        })
      }
    },
    getLabels(data) {
      return data.map((row) => row['Date'])
    },
    getData(data) {
      return data.map((row) => (row['res'] || '').split(', ').map((n) => parseFloat(n)))
    },
  },
  computed: {
    serum_mg_data: function () {
      return this.getData(this.getChartData('beef_mg_results'))
    },
    get_mg_data: function () {
      return {
        labels: this.getLabels(this.getChartData('beef_mg_results')),
        datasets: [
          {
            label: 'Serum MG',
            borderWidth: 2,
            borderColor: 'rgba(0, 0, 0, .4)',
            backgroundColor: 'rgba(51, 122, 183, 0.6)',
            data: this.serum_mg_data,
          },
        ],
      }
    },
    sse_data: function () {
      return this.getData(this.getChartData('beef_sse_results'))
    },
    get_sse_data: function () {
      return {
        labels: this.getLabels(this.getChartData('beef_sse_results')),
        datasets: [
          {
            label: 'Serum SSE',
            borderWidth: 2,
            borderColor: 'rgba(0, 0, 0, .4)',
            backgroundColor: 'rgba(51, 122, 183, 0.6)',
            data: this.sse_data,
          },
        ],
      }
    },
    cu_data: function () {
      return this.getData(this.getChartData('beef_cu_results'))
    },
    get_cu_data: function () {
      return {
        labels: this.getLabels(this.getChartData('beef_cu_results')),
        datasets: [
          {
            label: 'Serum CU',
            borderWidth: 2,
            borderColor: 'rgba(0, 0, 0, .4)',
            backgroundColor: 'rgba(51, 122, 183, 0.6)',
            data: this.cu_data,
          },
        ],
      }
    },
  },
}
</script>
